import Table from '@packages/ui/table/table.tsx';
import {
  OnlineOrderConfirmationState,
  OnlineOrderDeliveryMode,
  OnlineOrderListItemFragment,
  OnlineOrderPaymentMethod,
  OnlineOrderState,
} from '@client/graphql/types/graphql.ts';
import TableHeaderColumn from '@packages/ui/table/table-header-column.tsx';
import TableRow from '@packages/ui/table/table-row.tsx';
import TableRowColumn from '@packages/ui/table/table-row-cell.tsx';
import moment from 'moment-timezone';
import { useFocusedCompanyContext } from '@packages/core/company/focused-company-context.ts';
import {
  BanknotesIcon,
  BuildingLibraryIcon,
  CreditCardIcon,
  ShoppingBagIcon,
  TruckIcon,
} from '@heroicons/react/24/outline';
import { useNavigate } from 'react-router-dom';
import OnlineOrderSetButtons from '@client/page/management/online-order/dashboard/component/order-online-state-button.tsx';
import { TableCellAlignment } from '@packages/ui/table/table-type.ts';
import classNames from 'classnames';
import { useTranslate } from '@tolgee/react';
import { groupBy } from 'graphql/jsutils/groupBy';
import { Fragment } from 'react';
import { TableSection } from '@packages/ui/table/table-section.tsx';
import CurrencyView from '@packages/core/utils/currency-view.tsx';

const OnlineOrderTable = (props: {
  orders: readonly OnlineOrderListItemFragment[];
  loading: boolean;
  navigationPrefix?: string;
  refetch: () => void;
  error?: string;
}) => {
  const { t } = useTranslate();
  const company = useFocusedCompanyContext();

  if (props.orders.length === 0) {
    return <div className="p-4 border-t">{t('online-order.dashboard.no-orders-found')}</div>;
  }

  const dateGroups = groupBy(props.orders, (item) => {
    return moment(item.userCreatedAt).tz(company.settings.timezone).format('DD MMM');
  });

  const dates = Array.from(dateGroups, ([date, onlineOrders]) => {
    const waitingTConfirmConfirmedOrders = Array.from(onlineOrders)
      .filter((item) => item.state == OnlineOrderState.WaitingForConfirmation)
      .sort((a, b) => {
        const firstArgCompletionTime = calculateCompletionTime(a, company.settings.timezone);
        const secondArgCompletionTime = calculateCompletionTime(b, company.settings.timezone);

        return moment(firstArgCompletionTime).isBefore(moment(secondArgCompletionTime)) ? -1 : 1;
      });

    const otherOrders = Array.from(onlineOrders)
      .filter((item) => item.state !== OnlineOrderState.WaitingForConfirmation)
      .sort((a, b) => {
        const firstArgCompletionTime = calculateCompletionTime(a, company.settings.timezone);
        const secondArgCompletionTime = calculateCompletionTime(b, company.settings.timezone);

        return moment(firstArgCompletionTime).isBefore(moment(secondArgCompletionTime)) ? -1 : 1;
      });

    return {
      date,
      onlineOrder: waitingTConfirmConfirmedOrders.concat(otherOrders),
    };
  });

  // Sort by date
  dates.sort((a, b) => {
    return moment(a.date).isBefore(moment(b.date)) ? 1 : -1;
  });

  return (
    <Table
      loading={props.loading}
      data={dates}
      error={props.error}
      refetch={props.refetch}
      build={(order) => {
        return (
          <Fragment key={order.date}>
            <TableSection key={order.date} colSpan={6}>
              <div className="flex justify-start py-1 sm:pl-2">
                <span className="font-bold text-blue-500">
                  {order.date == moment().tz(company.settings.timezone).format('DD MMM')
                    ? t('appointment.history.list.section.today', 'Today')
                    : order.date}
                </span>
              </div>
            </TableSection>

            {order.onlineOrder.map((onlineOrder) => (
              <OnlineOrderRow
                key={onlineOrder.id}
                order={onlineOrder}
                navigationPrefix={props.navigationPrefix}
                timezone={company.settings.timezone}
              />
            ))}
          </Fragment>
        );
      }}
    >
      {/*Mobile*/}
      <TableHeaderColumn className="sm:hidden w-10" alignment={TableCellAlignment.Center}>
        {t('common.table-header-column.type')}
      </TableHeaderColumn>
      <TableHeaderColumn className="sm:hidden w-16" alignment={TableCellAlignment.Left}>
        {t('common.table-header-column.status')}
      </TableHeaderColumn>
      <TableHeaderColumn className="sm:hidden w-24" alignment={TableCellAlignment.Left}>
        {t('common.table-header-column.guest-time', 'Start/ Desired')}
      </TableHeaderColumn>
      <TableHeaderColumn className="sm:hidden w-24">
        {t('common.table-header-column.name-message', 'Guest/Note')}
      </TableHeaderColumn>

      {/*Desktop*/}
      <TableHeaderColumn className="hidden sm:table-cell text-center">
        {t('common.table-header-column.type')}
      </TableHeaderColumn>
      <TableHeaderColumn className="hidden sm:table-cell  text-center">
        {t('common.table-header-column.guest')}
      </TableHeaderColumn>
      <TableHeaderColumn className="hidden sm:table-cell text-center">
        {t('common.table-header-column.guest-time', 'Start/ Desired')}
      </TableHeaderColumn>
      <TableHeaderColumn className="hidden sm:table-cell">{t('common.table-header-column.products')}</TableHeaderColumn>
      <TableHeaderColumn className="hidden sm:table-cell">{t('common.table-header-column.price')}</TableHeaderColumn>
      <TableHeaderColumn className="hidden sm:table-cell" alignment={TableCellAlignment.Right}></TableHeaderColumn>
    </Table>
  );
};

function OnlineOrderRow(props: {
  order: OnlineOrderListItemFragment;
  timezone: string;
  navigationPrefix: string | undefined;
}) {
  const navigate = useNavigate();
  const { t } = useTranslate();
  const { order, timezone } = props;
  const awaitingManualTransfer = isAwaitingManualTransferVerify(order);

  return (
    <TableRow
      key={order.id}
      onClick={() => {
        if (props.navigationPrefix) {
          navigate(`${props.navigationPrefix}/${order.id}`);
        } else {
          navigate(order.id);
        }
      }}
      className={classNames({
        'bg-amber-50': isWaitingForConfirm(order.state),
      })}
    >
      {/*Mobile*/}
      <TableRowColumn className="sm:hidden w-10">
        <DeliveryType deliveryMode={order.deliveryMode} distance={order.distanceToRestaurant} />
      </TableRowColumn>
      <TableRowColumn alignment={TableCellAlignment.Left} className="sm:hidden w-16">
        <OnlineOrderSetButtons
          navigationPrefix={props.navigationPrefix}
          onlineOrderId={order.id}
          onlineOrderState={order.state}
        />
      </TableRowColumn>
      <TableRowColumn className="sm:hidden" alignment={TableCellAlignment.Left}>
        <p className="text-gray-400">
          {order.expectedTime ? '' : moment(order.userCreatedAt).tz(timezone).format('HH:mm')}
        </p>
        {(order.estimatedDuration != null && order.confirmationState == OnlineOrderConfirmationState.Confirmed) ||
        order.estimatedDuration == 0 ? (
          <p className="font-bold">
            {order.expectedTime
              ? moment(order.expectedTime).tz(timezone).format('HH:mm')
              : moment(order.userCreatedAt).tz(timezone).add(order.estimatedDuration, 'minutes').format('HH:mm')}
          </p>
        ) : (
          <p className="text-gray-400">-- : --</p>
        )}
      </TableRowColumn>
      <TableRowColumn className="sm:hidden">
        <p className="font-semibold max-w-28 truncate">{order.guestName}</p>
        <div className="flex items-center gap-1">
          <PaymentMethodIcon paymentMethod={order.paymentMethod} />
          <p className="text-gray-500 text-sm max-w-28 truncate">{order.message}</p>
        </div>
        {awaitingManualTransfer && (
          <span className="mt-1 inline-block rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800 border border-yellow-300">
            {t(
              'online-order.dashboard.row.payment-needs-confirmation',
              'Payment proof confirmation needed',
            )}
          </span>
        )}
      </TableRowColumn>

      {/*Desktop*/}
      <TableRowColumn className="w-10 hidden sm:table-cell">
        <DeliveryType deliveryMode={order.deliveryMode} distance={order.distanceToRestaurant} />
      </TableRowColumn>
      <TableRowColumn className="hidden sm:table-cell text-center">
        <div className="flex flex-col items-center gap-1">
          <span>{order.guestName}</span>
          {awaitingManualTransfer && (
            <span className="inline-block rounded-full bg-yellow-100 px-2 py-0.5 text-xs font-semibold text-yellow-800 border border-yellow-300">
              {t(
                'online-order.dashboard.row.payment-needs-confirmation',
                'Payment proof confirmation needed',
              )}
            </span>
          )}
        </div>
      </TableRowColumn>
      <TableRowColumn className="hidden sm:table-cell text-center">
        <p className="text-gray-400">
          {order.expectedTime ? '' : moment(order.userCreatedAt).tz(timezone).format('HH:mm')}
        </p>
        {(order.estimatedDuration != null && order.confirmationState == OnlineOrderConfirmationState.Confirmed) ||
        order.estimatedDuration == 0 ? (
          <p className="font-bold">
            {order.expectedTime
              ? moment(order.expectedTime).tz(timezone).format('HH:mm')
              : moment(order.userCreatedAt).tz(timezone).add(order.estimatedDuration, 'minutes').format('HH:mm')}
          </p>
        ) : (
          <p className="text-gray-400">-- : --</p>
        )}
      </TableRowColumn>
      <TableRowColumn className="hidden md:table-cell">{order.summary.itemsCount} product(s)</TableRowColumn>
      <TableRowColumn className="hidden md:table-cell">
        <div className="flex items-center gap-2">
          <PaymentMethodIcon paymentMethod={order.paymentMethod} />
          <CurrencyView price={order.totalPrice} />
        </div>
      </TableRowColumn>
      <TableRowColumn className="hidden sm:table-cell" alignment={TableCellAlignment.Right}>
        <OnlineOrderSetButtons
          navigationPrefix={props.navigationPrefix}
          onlineOrderId={order.id}
          onlineOrderState={order.state}
        />
      </TableRowColumn>
    </TableRow>
  );
}

const PaymentMethodIcon = (props: { paymentMethod: OnlineOrderPaymentMethod | null | undefined }) => {
  const paymentMethod = props.paymentMethod;
  switch (paymentMethod) {
    case OnlineOrderPaymentMethod.Cash:
      return <BanknotesIcon className="w-4 h-4" />;
    case OnlineOrderPaymentMethod.Stripe:
      return <CreditCardIcon className="w-4 h-4" />;
    case OnlineOrderPaymentMethod.CardAtPlace:
      return <CreditCardIcon className="w-4 h-4" />;
    case OnlineOrderPaymentMethod.ManualBankTransfer:
      return <BuildingLibraryIcon className="w-4 h-4" />;
    default:
      return null;
  }
};

const DeliveryType = (props: { deliveryMode: OnlineOrderDeliveryMode; distance: number | null | undefined }) => {
  let additionalInfo = '';
  if (props.deliveryMode === OnlineOrderDeliveryMode.Delivery && props.distance) {
    additionalInfo = ` ${(props.distance / 1000).toFixed(2)} km`;
  }

  return (
    <div className="flex flex-col items-center">
      {props.deliveryMode == OnlineOrderDeliveryMode.Delivery ? (
        <>
          <TruckIcon className="w-6 h-6" />{' '}
          <p className="inline-block text-center text-xs md:text-sm line-clamp-1">{additionalInfo}</p>
        </>
      ) : (
        <ShoppingBagIcon className="w-6 h-6" />
      )}
    </div>
  );
};

const isWaitingForConfirm = (onlineOrderState: OnlineOrderState) => {
  return onlineOrderState === OnlineOrderState.WaitingForConfirmation;
};

const isAwaitingManualTransferVerify = (order: OnlineOrderListItemFragment) => {
  return (
    Boolean(order.manualTransferProofSubmittedAt) &&
    !order.manualTransferConfirmedAt &&
    order.confirmationState !== OnlineOrderConfirmationState.Canceled
  );
};

function calculateCompletionTime(onlineOrder: OnlineOrderListItemFragment, timezone: string) {
  const expectedTime = onlineOrder.expectedTime;
  const createdAt = onlineOrder.createdAt || onlineOrder.userCreatedAt;
  const estimatedDuration = onlineOrder.estimatedDuration;

  if (expectedTime) {
    return moment(expectedTime).tz(timezone);
  }

  return moment(createdAt).add(estimatedDuration, 'minutes').tz(timezone);
}

export default OnlineOrderTable;
